<template>
  <ComboboxRoot
    :open="isOpen"
    :model-value="null"
    :ignore-filter="true"
    :highlight-on-hover="false"
    :reset-search-term-on-blur="false"
    :reset-search-term-on-select="false"
    :disabled="disabled || readonly"
    class="autocomplete-root"
    @update:open="onOpenChange"
    @update:model-value="onPick"
    @highlight="onHighlight"
    @keydown.capture="onKeydownCapture"
  >
    <ComboboxAnchor as-child>
      <ComboboxInput
        :id="id || undefined"
        v-model="text"
        class="input"
        :class="[{ 'is-danger': hasError }, inputClass]"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        @focus="onFocus"
        @blur="onBlur"
      />
    </ComboboxAnchor>
    <ComboboxPortal>
      <ComboboxContent
        position="popper"
        align="start"
        :side-offset="4"
        :collision-padding="8"
        class="autocomplete-content"
      >
        <!-- Outside the viewport: stays visible while the list scrolls -->
        <div v-if="isLoading" class="dropdown-progress-wrap">
          <ProgressBar />
        </div>
        <ComboboxViewport ref="viewportRef" class="autocomplete-viewport">
          <!-- TransitionGroup animates reorders (the "move" slide) while the
               user searches. Its move/FLIP measures viewport coordinates, so
               it must never span a popover reposition — we guarantee that by
               closing instantly and clearing results on close (see the isOpen
               watcher), so every open starts from fresh item nodes that just
               "enter" rather than persisting across reka's repositioning. -->
          <TransitionGroup name="ac-list" tag="div" class="autocomplete-items">
            <ComboboxItem
              v-for="item in displayOptions"
              :key="item.key"
              :value="item.option"
              class="dd-item"
              :class="{
                'is-selected': item.selected,
                'is-highlighted': isNavigating && item.key === highlightedKey,
              }"
            >
              {{ item.label }}
            </ComboboxItem>
          </TransitionGroup>
          <div v-if="showResultsSummary" class="dropdown-notice">
            Showing
            <b>{{ options.length }}</b>
            of
            <b>{{ totalResults }}</b>
            results — refine your search to see more
          </div>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<script>
/**
 * AutocompleteInput — a plain text input with server-driven suggestions.
 *
 * Unlike SelectContainer (vue-multiselect), the typed text IS the value:
 * suggestions are assisted typing, not a constrained choice. Picking a
 * suggestion writes its label into the input; free text stays untouched
 * on blur/Escape.
 *
 * Events:
 *   input(string)  — every genuine user text change (keystroke or pick);
 *                    NOT fired for value pushed down from the parent.
 *   change(string) — commit of free text (Enter, or blur when changed).
 *   select(option) — a suggestion object was picked (carries the raw option
 *                    so callers can key off its real value, not the label).
 *
 * onSearch(query, abortSignal) must resolve to an axios response whose
 * data is { results, total, total_pages } (same contract as SelectContainer).
 */
import { debounce } from 'lodash-es'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxViewport,
} from 'reka-ui'
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'

const LABEL_FALLBACKS = ['choice_label', 'label', 'name', 'username', 'id']

export default {
  name: 'AutocompleteInput',
  components: {
    ComboboxAnchor,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxPortal,
    ComboboxRoot,
    ComboboxViewport,
  },
  props: {
    // String is the native shape; objects are tolerated for legacy stored
    // option values ({ id, name, ... }) and resolved to their display label.
    value: { type: [String, Object], default: '' },
    onSearch: { type: Function, required: true },
    // Forwarded to the <input> so a <label for="..."> can target it
    id: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    // Label field name override; falls back to choice_label|label|name|username|id
    label: { type: String, default: null },
    // Extra class(es) applied to the inner <input> (the component root is
    // the ComboboxRoot wrapper, so class fallthrough can't reach the input)
    inputClass: { type: [String, Object, Array], default: null },
    debounceMs: { type: Number, default: 700 },
  },
  emits: ['input', 'change', 'select'],
  setup(props, { emit }) {
    function getLabel(option) {
      if (option == null) return ''
      if (typeof option === 'string') return option
      if (
        props.label &&
        option[props.label] != null &&
        option[props.label] !== ''
      )
        return String(option[props.label])
      for (const key of LABEL_FALLBACKS) {
        if (option[key] != null && option[key] !== '')
          return String(option[key])
      }
      return ''
    }

    // Stable keys (no index component) so the TransitionGroup can track an
    // item across result updates and animate its move to a new row
    function optionKey(option) {
      if (option && typeof option === 'object' && option.id != null)
        return String(option.id)
      return getLabel(option)
    }

    const text = ref(getLabel(props.value) || '')
    const options = ref([])
    const totalResults = ref(0)
    const totalPages = ref(0)
    const isLoading = ref(false)
    const isOpen = ref(false)
    const highlightedKey = ref(null)
    const viewportRef = ref(null)

    // A new result set is a new list — scroll it back to the top so a shrunk
    // list doesn't leave the viewport parked past its (now shorter) content.
    watch(options, () => {
      nextTick(() => {
        const el = viewportRef.value?.$el
        if (el) el.scrollTop = 0
      })
    })

    // Resolve label/key/selected once per options-or-text change instead of
    // calling getLabel several times per option on every render.
    const displayOptions = computed(() => {
      const current = text.value.trim()
      return options.value.map((option) => {
        const label = getLabel(option)
        return {
          option,
          key: optionKey(option),
          label,
          // Case-sensitive: "10x genomics"/"10x Genomics"/"10X Genomics" coexist
          selected: label.trim() === current,
        }
      })
    })

    // Text changes coming DOWN from the parent must be silent (no input echo,
    // no search); genuine user edits (keystroke, pick) are not. See the text
    // watcher — the flags are consumed there because it runs async.
    let syncingFromProp = false
    let skipSearchAfterPick = false

    watch(
      () => props.value,
      (val) => {
        const label = getLabel(val) || ''
        if (label !== text.value) {
          syncingFromProp = true
          text.value = label
        }
      },
    )

    // ─── Server search (same plumbing as SelectContainer.doSearch) ──────
    let searchController = null

    // Feedback must be immediate: the bar shows from the keystroke that
    // scheduled a search until the last request settles (see doSearch).
    // lodash-es 4 has no debounced.pending(), so track it by hand.
    let searchScheduled = false

    function startSearching(query) {
      isLoading.value = true
      searchScheduled = true
      debouncedSearch(query)
    }

    async function doSearch(query) {
      searchScheduled = false
      isLoading.value = true
      searchController?.abort()
      searchController = new AbortController()
      const { signal } = searchController
      try {
        const response = await props.onSearch(query, signal)
        // The leading-edge request can resolve while the user has already
        // typed past its query — drop a response the input no longer matches
        // so stale suggestions never show against newer text.
        if (query !== text.value) return
        if (response?.status === 200) {
          // ComboboxItem rejects '' as a value (reserved for clearing the
          // selection) — drop options with no resolvable label. Assign the
          // server's list as-is to keep its relevance ordering; keyed items
          // that persist keep their DOM node (no re-animation).
          options.value = (response.data.results || []).filter(
            (option) => getLabel(option) !== '',
          )
          totalResults.value = response.data.total || 0
          totalPages.value = response.data.total_pages || 0
          openIfAllowed()
        }
      } catch (err) {
        // axios throws CanceledError on abort; fetch throws AbortError
        if (err.name !== 'CanceledError' && err.name !== 'AbortError')
          console.warn('AutocompleteInput: search failed', err)
      } finally {
        // The loading flag is raised at the keystroke, not here — only the
        // latest request may lower it, and not while a debounced follow-up
        // search is still scheduled
        if (searchController?.signal === signal && !searchScheduled)
          isLoading.value = false
      }
    }

    const debouncedSearch = debounce(doSearch, props.debounceMs, {
      leading: true,
      trailing: true,
    })

    function stopSearching() {
      debouncedSearch.cancel()
      searchController?.abort()
      searchScheduled = false
      isLoading.value = false
    }

    onUnmounted(stopSearching)

    // ─── Open state ──────────────────────────────────────────────────────
    // The panel only shows when it has something in it, the input is
    // focused, and the user hasn't just validated a suggestion (after a
    // pick it stays closed until the input is refocused or edited).
    const hasContent = computed(() => options.value.length > 0)
    const isFocused = ref(false)
    let suppressOpen = false

    function openIfAllowed() {
      if (suppressOpen || !isFocused.value) return
      if (!hasContent.value && !isLoading.value) return
      isOpen.value = true
    }

    watch([hasContent, isLoading], ([has, loading]) => {
      if (!has && !loading) isOpen.value = false
    })

    // Drop results when the panel closes so the next open renders fresh item
    // nodes. This is what keeps the reorder "move" animation safe: the item
    // DOM never persists across reka repositioning the popover on (re)open —
    // fresh items just "enter", they don't "move" from the park position.
    watch(isOpen, (open) => {
      if (!open) options.value = []
    })

    function onOpenChange(open) {
      if (open) {
        // reka requests open on any keydown/click while closed — apply the
        // same gating as our own opens
        openIfAllowed()
        return
      }
      // Close requests (Escape, outside click, blur) are always honored
      isOpen.value = false
      stopSearching()
      isNavigating.value = false
    }

    // reka auto-highlights the first item whenever results appear, which
    // (a) would make Enter silently pick suggestion #1 while the user is
    // just typing, and (b) makes the first option look selected on focus.
    // isNavigating gates both: Enter only picks after explicit arrow
    // navigation, and the highlight ring is only drawn while navigating.
    const isNavigating = ref(false)

    watch(text, (query) => {
      // Value pushed down from the parent: sync only, no echo, no search
      if (syncingFromProp) {
        syncingFromProp = false
        return
      }
      // Genuine user change (keystroke or pick) — the value IS the text
      emit('input', query)
      if (skipSearchAfterPick) {
        skipSearchAfterPick = false
        return
      }
      // Editing the text is a new query — lift the post-pick suppression
      suppressOpen = false
      isNavigating.value = false
      // Optimistic filtering: narrow the already-loaded options right away;
      // the server response then confirms them and appends new matches
      const q = query.trim().toLowerCase()
      if (q) {
        const filtered = options.value.filter((o) =>
          getLabel(o).toLowerCase().includes(q),
        )
        if (filtered.length !== options.value.length) options.value = filtered
      }
      startSearching(query)
      openIfAllowed()
    })

    function onHighlight(item) {
      const val = item?.value
      highlightedKey.value = val == null ? null : optionKey(val)
    }

    // Commit the typed free text — but only when it differs from the value
    // the parent already holds, so blur/Enter on an unchanged field don't
    // re-fire (which, for the column filter, would reset paging and refetch).
    function commit() {
      if (text.value !== (getLabel(props.value) || ''))
        emit('change', text.value)
    }

    function onKeydownCapture(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!isOpen.value) {
          // Arrowing on a closed dropdown is an explicit ask to reopen —
          // lifts the post-pick suppression and refreshes the suggestions
          suppressOpen = false
          startSearching(text.value || '')
          openIfAllowed()
        } else if (!isNavigating.value && highlightedKey.value != null) {
          // reka has already (invisibly) highlighted the first item; the
          // first arrow press just reveals it instead of moving past it
          event.preventDefault()
          event.stopPropagation()
        }
        isNavigating.value = true
        return
      }
      // Enter (composition-safe) commits the typed text unless the user has
      // arrow-navigated onto a suggestion (then reka picks it).
      if (event.key === 'Enter' && !event.isComposing && !isNavigating.value) {
        // preventDefault so it can't also submit an enclosing <form>;
        // stopPropagation so reka doesn't select the auto-highlighted item.
        event.preventDefault()
        event.stopPropagation()
        suppressOpen = true
        isOpen.value = false
        isNavigating.value = false
        stopSearching()
        commit()
      }
    }

    function onFocus() {
      isFocused.value = true
      if (props.readonly || props.disabled) return
      // Refocusing lifts the post-pick suppression and fetches fresh
      // suggestions; the panel opens when results arrive
      suppressOpen = false
      startSearching(text.value || '')
      openIfAllowed()
    }

    function onBlur() {
      isFocused.value = false
      // Leaving the field commits the typed text if it changed (callers like
      // the column filter apply/remove the filter on this)
      commit()
    }

    // ComboboxRoot's model is pinned to null (see template): a pick is a
    // one-shot event, never a lasting selection — otherwise reka keeps the
    // picked item [data-state=checked] on later searches whatever the text
    // says. We emit 'select' with the raw option so callers can key off its
    // real value (e.g. a username), not the display label.
    function onPick(option) {
      if (option == null) return
      const label = getLabel(option)
      // Only flag/rewrite when the label actually differs — otherwise the
      // text watcher never fires and the flag would go stale, swallowing the
      // next real keystroke's search.
      if (label !== text.value) {
        skipSearchAfterPick = true
        text.value = label
      }
      // Validated: stay closed until the input is refocused or edited.
      suppressOpen = true
      isOpen.value = false
      isNavigating.value = false
      stopSearching()
      emit('select', option)
    }

    const showResultsSummary = computed(
      () => !isLoading.value && totalPages.value > 1,
    )

    return {
      text,
      options,
      displayOptions,
      totalResults,
      isLoading,
      isOpen,
      isNavigating,
      highlightedKey,
      viewportRef,
      showResultsSummary,
      onOpenChange,
      onHighlight,
      onFocus,
      onBlur,
      onPick,
      onKeydownCapture,
    }
  },
}
</script>

<style scoped>
.autocomplete-root {
  width: 100%;
}

.autocomplete-root .input {
  width: 100%;
}
</style>

<!-- Portal styles in ui-elements.scss (.autocomplete-content) -->
